import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { trackLead, trackCompleteRegistration } from '@/lib/metaPixel';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Configure axios defaults
axios.defaults.timeout = 15000; // 15 second timeout

// Configure axios to always send auth header
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for better error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clean stale/invalid redirect values on app start
    const staleRedirect = localStorage.getItem('redirect_after_login');
    if (staleRedirect && !staleRedirect.startsWith('/')) {
      localStorage.removeItem('redirect_after_login');
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      
      if (!storedToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        timeout: 10000
      });
      
      if (response.data && response.data.user_id) {
        setUser(response.data);
        
        // Track login/registration with Meta Pixel
        const isNewUser = response.data.trial_status?.trial_days_remaining === 7;
        if (isNewUser) {
          trackCompleteRegistration({ method: 'google' });
        }
        trackLead({ user_type: response.data.subscription_type || 'free' });
      } else {
        localStorage.removeItem('session_token');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Only clear token if it's an auth error (401), not network error
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (redirectTo = null) => {
    // Guard: if called from onClick, redirectTo is a SyntheticEvent, not a string
    const destination = (typeof redirectTo === 'string') ? redirectTo : '/expenses';
    
    // Check if user is already logged in
    const existingToken = localStorage.getItem('session_token');
    if (existingToken && user) {
      window.location.href = destination;
      return;
    }
    
    // Clear any old session data
    sessionStorage.removeItem('used_sessions');
    localStorage.removeItem('session_token');
    
    // Store the redirect destination after login
    localStorage.setItem('redirect_after_login', destination);
    
    // Use current origin for redirect
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      localStorage.removeItem('redirect_after_login');
      sessionStorage.removeItem('used_sessions');
      setUser(null);
    }
  };

  const exchangeSession = async (sessionId) => {
    console.log('Starting session exchange with API:', API);
    
    try {
      const response = await axios.post(`${API}/auth/session`, 
        { session_id: sessionId },
        { 
          timeout: 20000,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      console.log('Session exchange successful');
      
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        
        const { session_token, ...userData } = response.data;
        setUser(userData);
        
        console.log('User logged in:', userData.email);
        
        // Migrate local expenses in background (non-blocking)
        migrateLocalData(session_token);
        
        return userData;
      } else {
        throw new Error('No session token in response');
      }
    } catch (error) {
      console.error('Session exchange failed:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      localStorage.removeItem('session_token');
      throw error;
    }
  };

  const migrateLocalData = async (sessionToken) => {
    try {
      const localExpenses = localStorage.getItem('evite_dividas_local_expenses');
      if (!localExpenses) return;

      const expenses = JSON.parse(localExpenses);
      if (!expenses || expenses.length === 0) return;

      console.log(`Migrating ${expenses.length} local expenses...`);

      const migrateResponse = await axios.post(`${API}/expenses/migrate-local`, 
        { expenses },
        { 
          headers: { 
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json' 
          },
          timeout: 30000
        }
      );
      
      if (migrateResponse.data.success && migrateResponse.data.migrated_count > 0) {
        localStorage.removeItem('evite_dividas_local_expenses');
        localStorage.removeItem('evite_dividas_local_meta');
        localStorage.removeItem('evite_dividas_savings_goal');
        
        toast.success(`${migrateResponse.data.migrated_count} despesa(s) migrada(s) para sua conta!`, {
          duration: 5000
        });
      }
    } catch (migrateError) {
      console.error('Migration error:', migrateError.message);
      // Migration failure doesn't block login
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      exchangeSession, 
      checkAuth,
      refreshUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
